import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { ArrowRight, MapPin, Navigation, Building2 } from "lucide-react";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal } from "../components/shared/kursusUi";

const AddressCard = ({ id, name, lines, query, badge }) => {
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
  const dirHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;

  return (
    <Reveal
      className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_8px_30px_rgba(15,40,90,0.06)] overflow-hidden grid md:grid-cols-2 transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(15,40,90,0.13)]"
      data-testid={`address-card-${id}`}
    >
      {/* Info */}
      <div className="p-6 sm:p-8 flex flex-col justify-center">
        {badge && (
          <span
            className="self-start mb-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: "hsl(210, 100%, 45%, 0.1)", color: PRIMARY }}
          >
            <Building2 className="h-3.5 w-3.5" />
            {badge}
          </span>
        )}
        <h3 className="font-display text-xl font-medium text-slate-900">
          {name}
        </h3>
        <div className="mt-3 flex items-start gap-2 text-slate-600">
          <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: PRIMARY }} />
          <div>
            {lines.map((l, i) => (
              <p key={i} className="leading-relaxed">{l}</p>
            ))}
          </div>
        </div>
        <a
          href={dirHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: PRIMARY }}
          data-testid={`address-directions-${id}`}
        >
          <Navigation className="h-4 w-4" />
          Få vejvisning
        </a>
      </div>

      {/* Map */}
      <div className="min-h-[260px] md:min-h-[300px] bg-slate-100">
        <iframe
          title={`Kort: ${name}`}
          src={mapSrc}
          className="w-full h-full min-h-[260px]"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </Reveal>
  );
};

const AddressesPage = () => {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
      <div className="votra-grain" aria-hidden />
      <Seo
        title="Votras adresser i Ringsted og Søborg | Votra"
        description="Find vej til Votra Danmark ApS. Vi har lokationer i Ringsted og Søborg, samt undervisning hos UCplus i Skovlunde og Højbjerg."
        path="/adresser"
        jsonLd={[
          breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Adresser", path: "/adresser" }]),
        ]}
      />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-16 md:py-24 text-center">
          <Reveal className="flex justify-center">
            <span className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-4">
              <MapPin className="h-7 w-7 text-white" />
            </span>
          </Reveal>
          <Reveal i={1} className="flex justify-center"><Overline light>Find vej</Overline></Reveal>
          <Reveal i={2}>
            <h1 className="font-display text-4xl md:text-5xl font-medium text-white leading-tight mb-5">Vores adresser</h1>
          </Reveal>
          <Reveal i={3}>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Find vej til Votra Danmark ApS og vores undervisningssteder hos UCplus
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
        <div className="max-w-5xl mx-auto space-y-12">
          <section>
            <Reveal>
              <Overline>Votra Danmark ApS</Overline>
              <h2 className="font-display text-2xl md:text-3xl font-medium mb-6 text-slate-900">
                Votra Danmark ApS
              </h2>
            </Reveal>
            <div className="space-y-6">
              <AddressCard
                id="votra-soborg-transformervej"
                name="Votra Danmark ApS, Søborg"
                badge="Hovedkontor"
                lines={["Transformervej 14", "2860 Søborg"]}
                query="Transformervej 14, 2860 Søborg, Danmark"
              />
              <AddressCard
                id="votra-ringsted"
                name="Votra Danmark ApS, Ringsted"
                lines={["Sct. Hansgade 9, 2. sal", "4100 Ringsted"]}
                query="Sct. Hansgade 9, 2. sal, 4100 Ringsted, Danmark"
              />
              <AddressCard
                id="votra-soborg-vandtaarnsvej"
                name="Votra Danmark ApS, Søborg"
                lines={["Vandtårnsvej 77", "2860 Søborg"]}
                query="Vandtårnsvej 77, 2860 Søborg, Danmark"
              />
            </div>
            <div className="mt-6 flex flex-wrap gap-4" data-testid="addresses-location-links">
              <Link
                to="/lokationer/soeborg"
                className="inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all"
                style={{ color: PRIMARY }}
                data-testid="addresses-link-soeborg"
              >
                Læs om Votra Søborg <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/lokationer/ringsted"
                className="inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all"
                style={{ color: PRIMARY }}
                data-testid="addresses-link-ringsted"
              >
                Læs om Votra Ringsted <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>

          <section>
            <Reveal>
              <Overline>UCplus</Overline>
              <h2 className="font-display text-2xl md:text-3xl font-medium mb-6 text-slate-900">
                UCplus
              </h2>
            </Reveal>
            <div className="space-y-6">
              <AddressCard
                id="ucplus-skovlunde"
                name="UCplus, Skovlunde"
                badge="UCplus"
                lines={["Mileparken 12A", "2740 Skovlunde"]}
                query="Mileparken 12A, 2740 Skovlunde, Danmark"
              />
              <AddressCard
                id="ucplus-hojbjerg"
                name="UCplus, Højbjerg"
                badge="UCplus"
                lines={["Øster Parkvej 15", "8270 Højbjerg"]}
                query="Øster Parkvej 15, 8270 Højbjerg, Danmark"
              />
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AddressesPage;
